import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import moment from 'moment'
import { ArrowLeft, Check, Copy } from 'lucide-react'
import Input from '../components/Input'
import Select from '../components/Select'
import Loading from '../components/Loading'
import {
  addCustomer, fetchAttendance, getUserByID, removeCustomer,
  resetDeviceID, resetPassword, resignUser, updateUser,
} from '../actions/userActions'
import {
  ADD_CUSTOMER_TO_USER_RESET, FETCH_ATTENDANCE_RESET, GET_USER_BY_ID_RESET,
  REMOVE_CUSTOMER_FROM_USER_RESET, RESET_DEVICE_ID_RESET, RESET_PASSWORD_RESET,
  RESIGN_USER_RESET, UPDATE_USER_RESET,
} from '../constants/userConstants'

const btnPrimary = 'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnOutline = 'border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnDanger = 'bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const btnWarning = 'bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 text-gray-800 text-sm font-medium px-4 py-2 rounded-lg transition-colors';
const visibleEmail = email => /@local\.invalid$/i.test(email || '') ? '' : (email || '');

function Modal({ open, onClose, title, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">{footer}</div>}
      </div>
    </div>
  );
}

const UserByID = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id: rawId } = useParams()
  const id = rawId.replace(':', '')

  const [name, setName] = useState('')
  const [deviceInfo, setDeviceInfo] = useState(null)
  const [roleName, setRoleName] = useState('')
  const [email, setEmail] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [merchantId, setMerchantId] = useState('')
  const [ipAddress, setIpAddress] = useState('')
  const [status, setStatus] = useState(true)
  const [joinedOn, setJoinedOn] = useState('')
  const [resignedOn, setResignedOn] = useState('')
  const [rolesFromServer, setRolesFromServer] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [role, setRole] = useState('')
  const [calling, setCalling] = useState(false)

  const [newPassword, setNewPassword] = useState('')
  const [passwordModal, setPasswordModal] = useState(false)
  const [copiedPassword, setCopiedPassword] = useState(false)
  const [resignModal, setResignModal] = useState(false)
  const [resetDeviceModal, setResetDeviceModal] = useState(false)

  const [userCustomers, setUserCustomers] = useState([])
  const [deleteCustomerModal, setDeleteCustomerModal] = useState(false)
  const [deleteCustomerID, setDeleteCustomerID] = useState('')
  const [addCustomerModal, setAddCustomerModal] = useState(false)
  const [addCustomerName, setAddCustomerName] = useState('')
  const [customerOptions, setCustomerOptions] = useState([])

  const [attenance, setAttendance] = useState([])
  const [tasks, setTasks] = useState([])
  const [date, setDate] = useState('')

  const [activeTab, setActiveTab] = useState('settings')
  const [updateUserOption, setUpdateUserOption] = useState(false)
  const [loading, setLoading] = useState(true)

  const { userInfo } = useSelector(state => state.userLogin)
  const { getRoleData } = useSelector(state => state.getRoleInfo)

  useEffect(() => { if (!userInfo) navigate('/') }, [userInfo])
  useEffect(() => {
    if (getRoleData) {
      setUpdateUserOption(getRoleData.usersUpdate)
      if (!getRoleData.usersView) { toast('Not Authorized', { type: 'error', hideProgressBar: true, autoClose: 2000 }); navigate('/') }
    }
  }, [getRoleData])

  useEffect(() => { dispatch(getUserByID(id)) }, [])

  const { errorGetUserByIDInfo, getUserByIDData } = useSelector(state => state.getUserByIDInfo)
  useEffect(() => {
    dispatch({ type: GET_USER_BY_ID_RESET })
    if (getUserByIDData) {
      setLoading(false)
      const u = getUserByIDData.user || {}
      const r = u.role || {}
      setName(u.name || ''); setEmail(visibleEmail(u.email)); setIdentifier(u.identifier || '')
      setIpAddress(u.ipAddress || ''); setRoleName(r.name || ''); setStatus(u.status ?? true)
      setJoinedOn(u.joinedOn || ''); setResignedOn(u.resignedOn || ''); setRole(r._id || '')
      setMerchantId(u.merchantId?._id || u.merchantId || '')
      setCalling(u.calling ?? false); setDeviceInfo(u.deviceInfo || null)
      setRolesFromServer(getUserByIDData.roles || [])
      setUserCustomers(getUserByIDData.userCustomers || [])
      const allCustomers = getUserByIDData.customers || []
      setCustomerOptions(allCustomers.filter(Boolean).map(c => ({ id: c._id, title: c.name || '—' })))
      setAttendance(getUserByIDData.attendance || [])
      setTasks(getUserByIDData.tasks || [])
    } else if (errorGetUserByIDInfo) {
      setLoading(false)
      if (errorGetUserByIDInfo.includes('Cast')) { navigate('/'); return }
      toast(errorGetUserByIDInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
    }
  }, [getUserByIDData, errorGetUserByIDInfo])

  useEffect(() => {
    if (rolesFromServer?.length) setRoleOptions(rolesFromServer.filter(Boolean).map(r => ({ id: r._id, title: r.name || '—' })))
  }, [rolesFromServer])

  const updateUserHandler = () => {
    if (!name || !role || !ipAddress || !identifier.trim()) { toast('Name, identifier, role and IP address are mandatory', { type: 'error', hideProgressBar: true, autoClose: 2000 }); return }
    dispatch(updateUser(id, name, role, Boolean(calling), email, ipAddress, 0, identifier.trim()))
  }
  const { loadingUpdateUserInfo, errorUpdateUserInfo, updateUserData } = useSelector(state => state.updateUserInfo)
  useEffect(() => {
    dispatch({ type: UPDATE_USER_RESET })
    if (updateUserData) { toast('User updated successfully', { type: 'success', hideProgressBar: true, autoClose: 2000 }); setTimeout(() => dispatch(getUserByID(id)), 1000) }
    else if (errorUpdateUserInfo) toast(errorUpdateUserInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [updateUserData, errorUpdateUserInfo])

  const resetPasswordHandler = () => dispatch(resetPassword(id))
  const { loadingResetPasswordInfo, resetPasswordData, errorResetPasswordInfo } = useSelector(state => state.resetPasswordInfo)
  useEffect(() => {
    dispatch({ type: RESET_PASSWORD_RESET })
    if (resetPasswordData) { toast('Password reset successful', { type: 'success', hideProgressBar: true, autoClose: 2000 }); setNewPassword(resetPasswordData); setCopiedPassword(false); setPasswordModal(true) }
    else if (errorResetPasswordInfo) toast(errorResetPasswordInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [resetPasswordData, errorResetPasswordInfo])

  const closePasswordModal = () => {
    setPasswordModal(false)
    setNewPassword('')
    setCopiedPassword(false)
  }

  const copyNewPassword = async () => {
    if (!newPassword) return
    try {
      await navigator.clipboard.writeText(newPassword)
      setCopiedPassword(true)
      setTimeout(() => setCopiedPassword(false), 1500)
    } catch {
      toast('Unable to copy password', { type: 'error', hideProgressBar: true, autoClose: 2000 })
    }
  }

  const resignHandler = () => dispatch(resignUser(id))
  const { loadingResignUser, errorResignUser, resignUserData } = useSelector(state => state.resignUserInfo)
  useEffect(() => {
    dispatch({ type: RESIGN_USER_RESET })
    if (resignUserData) { toast(resignUserData.msg, { type: 'success', hideProgressBar: true, autoClose: 2000 }); setResignModal(false); setTimeout(() => dispatch(getUserByID(id)), 1000) }
    else if (errorResignUser) toast(errorResignUser, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [resignUserData, errorResignUser])

  const resetDeviceHandler = () => dispatch(resetDeviceID(id))
  const { loadingResetDeviceIDInfo, errorResetDeviceIDInfo, resetDeviceIDData } = useSelector(state => state.resetDeviceIDInfo)
  useEffect(() => {
    dispatch({ type: RESET_DEVICE_ID_RESET })
    if (resetDeviceIDData) { toast('Device ID reset successfully', { type: 'success', hideProgressBar: true, autoClose: 2000 }); setResetDeviceModal(false); setTimeout(() => dispatch(getUserByID(id)), 1000) }
    else if (errorResetDeviceIDInfo) toast(errorResetDeviceIDInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [resetDeviceIDData, errorResetDeviceIDInfo])

  const addCustomerHandler = () => {
    if (!addCustomerName) { toast('Please select a customer', { type: 'error', hideProgressBar: true, autoClose: 2000 }); return }
    dispatch(addCustomer(id, addCustomerName))
  }
  const { loadingAddCustomerToUserInfo, errorAddCustomerToUserInfo, addCustomerToUserData } = useSelector(state => state.addCustomerToUserInfo)
  useEffect(() => {
    dispatch({ type: ADD_CUSTOMER_TO_USER_RESET })
    if (addCustomerToUserData) { toast('Customer added', { type: 'success', hideProgressBar: true, autoClose: 2000 }); setTimeout(() => { dispatch(getUserByID(id)); setAddCustomerModal(false); setAddCustomerName('') }, 1000) }
    else if (errorAddCustomerToUserInfo) toast(errorAddCustomerToUserInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [addCustomerToUserData, errorAddCustomerToUserInfo])

  const deleteCustomerHandler = () => dispatch(removeCustomer(id, deleteCustomerID))
  const { loadingRemoveCustomerFromUserInfo, errorRemoveCustomerFromUserInfo, removeCustomerFromUserData } = useSelector(state => state.removeCustomerFromUserInfo)
  useEffect(() => {
    dispatch({ type: REMOVE_CUSTOMER_FROM_USER_RESET })
    if (removeCustomerFromUserData) { toast('Customer removed', { type: 'success', hideProgressBar: true, autoClose: 2000 }); setTimeout(() => { dispatch(getUserByID(id)); setDeleteCustomerModal(false); setDeleteCustomerID('') }, 1000) }
    else if (errorRemoveCustomerFromUserInfo) toast(errorRemoveCustomerFromUserInfo, { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }, [removeCustomerFromUserData, errorRemoveCustomerFromUserInfo])

  const processedAttendance = React.useMemo(() => {
    if (!attenance?.length) return []
    return attenance.flatMap(day => {
      const ins = Array.isArray(day.punchIns) ? day.punchIns : []
      const outs = Array.isArray(day.punchOuts) ? day.punchOuts : []
      return ins.map((pIn, i) => ({ date: day.date, inTime: pIn.time, outTime: outs[i]?.time || 'N/A', inAddr: pIn.address, outAddr: outs[i]?.address || 'N/A' }))
    })
  }, [attenance])

  const { loadingFetchAttendance, fetchAttendanceData } = useSelector(state => state.fetchAttendanceInfo)
  const searchAttendance = () => {
    if (date) dispatch(fetchAttendance(id, moment(date).format('YYYY-MM-DD')))
    else toast('Enter a valid date', { type: 'error', hideProgressBar: true, autoClose: 2000 })
  }

  if (loading) return <Loading />

  const tabs = [
    { key: 'settings', label: 'Settings' },
    { key: 'customers', label: `Customers (${userCustomers.length})` },
    { key: 'attendance', label: 'Attendance' },
    { key: 'tasks', label: `Tasks (${tasks.length})` },
  ]

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Link to="/users" className="inline-flex items-center gap-2 mb-4 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">
        <ArrowLeft size={18} /> Back to Users
      </Link>
      {/* Profile Header */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-6 items-center">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-2xl font-bold text-white uppercase flex-shrink-0">
            {name?.[0] || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold text-gray-800">{name}</h1>
              {deviceInfo && <span className="text-sm text-gray-400">({deviceInfo.brand} {deviceInfo.model})</span>}
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-1">
              <div><span className="text-xs text-gray-400">Email</span><p className="text-sm font-medium text-gray-700 truncate">{email}</p></div>
              <div><span className="text-xs text-gray-400">Identifier</span><p className="text-sm font-medium text-gray-700">{identifier}</p></div>
              <div><span className="text-xs text-gray-400">Role</span><p className="text-sm font-medium text-gray-700">{roleName}</p></div>
              <div>
                <span className="text-xs text-gray-400">Status</span>
                <p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {status ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
              <div><span className="text-xs text-gray-400">Joined On</span><p className="text-sm font-medium text-gray-700">{joinedOn || '—'}</p></div>
              {!status && <div><span className="text-xs text-gray-400">Left On</span><p className="text-sm font-medium text-gray-700">{resignedOn || '—'}</p></div>}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-white rounded-xl shadow-sm p-1 w-fit">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-5">Settings</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <Input value={name} onChange={e => setName(e.target.value)} disabled={!updateUserOption} name="Name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identifier *</label>
              <Input
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                disabled={!updateUserOption}
                name="Identifier"
              />
              {merchantId && <p className="mt-1 text-xs text-gray-400">Linked merchant identifier</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <Select name="Role *" value={role} onChange={e => setRole(e.target.value)} options={roleOptions} disabled={!updateUserOption} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input value={email} onChange={e => setEmail(e.target.value)} disabled={!updateUserOption} name="Email" type="email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IP Address *</label>
              <Input value={ipAddress} onChange={e => setIpAddress(e.target.value)} disabled={!updateUserOption} name="IP Address" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {userInfo?.role?.settings && (
              <button onClick={updateUserHandler} disabled={!updateUserOption || loadingUpdateUserInfo} className={btnPrimary}>
                {loadingUpdateUserInfo ? 'Updating…' : 'Update'}
              </button>
            )}
            <button onClick={resetPasswordHandler} disabled={!updateUserOption} className={btnWarning}>
              Reset Password
            </button>
            {updateUserOption && (
              <button onClick={() => setResetDeviceModal(true)} className={btnOutline}>
                Reset Device ID
              </button>
            )}
            {userInfo?.role?.settings && (
              <button onClick={() => setResignModal(true)} disabled={!updateUserOption} className={btnDanger}>Leave</button>
            )}
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Customers</h2>
            <button onClick={() => setAddCustomerModal(true)} className={btnPrimary}>+ Add Customer</button>
          </div>
          <table className="w-full">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {userCustomers.length === 0 ? (
                <tr><td colSpan={2} className="px-4 py-12 text-center text-gray-400 text-sm">No customers assigned</td></tr>
              ) : userCustomers.map(c => (
                <tr key={c._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{c.name}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setDeleteCustomerID(c._id); setDeleteCustomerModal(true) }}
                      className="text-red-600 hover:text-red-800 text-xs font-medium border border-red-200 hover:border-red-400 px-3 py-1 rounded-lg transition-colors">
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Attendance</h2>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
            <button onClick={searchAttendance} disabled={loadingFetchAttendance} className={`${btnPrimary} py-1.5`}>Search</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {['Date', 'Punch In', 'In Location', 'Punch Out', 'Out Location'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {processedAttendance.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">No attendance records</td></tr>
                ) : processedAttendance.map((a, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">{a.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{a.inTime}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.inAddr || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{a.outTime}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.outAddr || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Tasks</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {['Task ID', 'Name', 'Customer', 'Type', 'Status', 'Allocated On'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasks.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">No tasks found</td></tr>
                ) : tasks.map(t => (
                  <tr key={t._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-blue-600">{t.taskID}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{t.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t.customer?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{t.status}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{t.allocatedOnFieldOn || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Password Modal */}
      <Modal open={passwordModal} onClose={closePasswordModal} title="New Password"
        footer={<button onClick={closePasswordModal} className={btnOutline}>Close</button>}>
        <p className="text-sm text-gray-600 mb-3">Password reset successfully. Save this password:</p>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 font-mono text-sm text-gray-800 select-all">
            {newPassword}
          </div>
          <button
            type="button"
            onClick={copyNewPassword}
            className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            aria-label="Copy password"
            title="Copy password"
          >
            {copiedPassword ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
          </button>
        </div>
      </Modal>

      {/* Resign Modal */}
      <Modal open={resignModal} onClose={() => setResignModal(false)} title="Resign Employee"
        footer={
          <>
            <button onClick={() => setResignModal(false)} className={btnOutline}>No</button>
            <button onClick={resignHandler} disabled={loadingResignUser} className={btnDanger}>
              {loadingResignUser ? 'Processing…' : 'Yes, Resign'}
            </button>
          </>
        }>
        <p className="text-sm text-gray-600">Are you sure you want to resign this employee? This action cannot be undone.</p>
      </Modal>

      {/* Delete Customer Modal */}
      <Modal open={deleteCustomerModal} onClose={() => { setDeleteCustomerModal(false); setDeleteCustomerID('') }} title="Remove Customer"
        footer={
          <>
            <button onClick={() => { setDeleteCustomerModal(false); setDeleteCustomerID('') }} className={btnOutline}>Cancel</button>
            <button onClick={deleteCustomerHandler} disabled={loadingRemoveCustomerFromUserInfo} className={btnDanger}>
              {loadingRemoveCustomerFromUserInfo ? 'Removing…' : 'Remove'}
            </button>
          </>
        }>
        <p className="text-sm text-gray-600">Remove this customer from the user's list?</p>
      </Modal>

      {/* Reset Device ID Modal */}
      <Modal open={resetDeviceModal} onClose={() => setResetDeviceModal(false)} title="Reset Device ID"
        footer={
          <>
            <button onClick={() => setResetDeviceModal(false)} className={btnOutline}>Cancel</button>
            <button onClick={resetDeviceHandler} disabled={loadingResetDeviceIDInfo} className={btnDanger}>
              {loadingResetDeviceIDInfo ? 'Resetting…' : 'Yes, Reset'}
            </button>
          </>
        }>
        <p className="text-sm text-gray-600">This will clear the stored device ID for this user. They will be able to log in from any device once. Are you sure?</p>
      </Modal>

      {/* Add Customer Modal */}
      <Modal open={addCustomerModal} onClose={() => { setAddCustomerModal(false); setAddCustomerName('') }} title="Add Customer"
        footer={
          <>
            <button onClick={() => { setAddCustomerModal(false); setAddCustomerName('') }} className={btnOutline}>Cancel</button>
            <button onClick={addCustomerHandler} disabled={loadingAddCustomerToUserInfo} className={btnPrimary}>
              {loadingAddCustomerToUserInfo ? 'Adding…' : 'Add'}
            </button>
          </>
        }>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer *</label>
          <Select name="Customer *" value={addCustomerName} onChange={e => setAddCustomerName(e.target.value)} options={customerOptions} />
        </div>
      </Modal>
    </div>
  )
}

export default UserByID
